/**
 * NotifQuickSheet — bottom sheet mobile con gli accessi rapidi a
 * Bacheca / Notifiche / Messaggi / Cerchia, più toggle tema e logout.
 *
 * Estratto da Navbar.tsx il 26 Ott 2026 (punto 7: refactor dei monoliti):
 * era un componente autonomo di ~200 righe dentro un file da 1.681.
 * Comportamento invariato: stesse props, stessa resa, stesso Escape/backdrop.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  LogOut,
  MessageSquare,
  MessagesSquare,
  Moon,
  Shield,
  Sun,
  UserRound,
  Users,
  X,
} from "lucide-react";
import useAuthStore from "@/store/useAuthStore";
import { useTheme } from "@/hooks/useTheme";

function ThemeToggleInline() {
  const { isDark, setTheme } = useTheme();
  return (
    <button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className="flex items-center gap-3 w-full px-2 py-2 text-neutral-400 hover:text-white active:text-white transition-colors text-sm"
      aria-label={isDark ? "Tema chiaro" : "Tema scuro"}
    >
      {isDark ? (
        <Sun size={18} className="text-yellow-400" />
      ) : (
        <Moon size={18} className="text-indigo-400" />
      )}
      <span>{isDark ? "Tema chiaro" : "Tema scuro"}</span>
    </button>
  );
}

export interface NotifQuickSheetProps {
  dmUnread: number;
  notifUnread: number;
  connectionRequests: number;
  onClose: () => void;
  onLogout: () => void;
}

export default function NotifQuickSheet({
  dmUnread,
  notifUnread,
  connectionRequests,
  onClose,
  onLogout,
}: NotifQuickSheetProps) {
  const navigate = useNavigate();
  // FIX AGENT-MENU (Set 2026): accesso rapido area agente da mobile
  const sheetUser = useAuthStore((s) => s.user);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  function go(path: string) {
    onClose();
    navigate(path);
  }

  return (
    <div
      className="md:hidden fixed inset-0 z-[60] flex items-end"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label="Accesso rapido notifiche"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full bg-neutral-900/95 backdrop-blur-3xl border-t border-white/10 rounded-t-3xl shadow-[0_-20px_60px_rgba(0,0,0,0.8)] px-6 pt-5 pb-10 animate-spring-up"
        style={{
          paddingBottom:
            "max(2.5rem, env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px)))",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 w-10 h-1 rounded-full bg-white/20" />

        <div className="flex items-center justify-between mb-5">
          <span className="text-white font-semibold text-base">
            Aggiornamenti
          </span>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-neutral-400 hover:text-white active:scale-90 transition-all"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => go("/board")}
            className="flex items-center gap-4 w-full p-4 rounded-xl bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all text-left"
          >
            <div className="relative flex-shrink-0">
              <MessageSquare size={22} className="text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">Bacheca</p>
              <p className="text-neutral-400 text-xs mt-0.5">
                Messaggi rapidi tra rider
              </p>
            </div>
            <span className="text-neutral-500 text-xs">›</span>
          </button>

          <button
            onClick={() => go("/notifications")}
            className="flex items-center gap-4 w-full p-4 rounded-xl bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all text-left"
          >
            <div className="relative flex-shrink-0">
              <Bell size={22} className="text-orange-400" />
              {notifUnread > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-black text-[10px] font-extrabold flex items-center justify-center">
                  {notifUnread > 99 ? "99+" : notifUnread}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">Notifiche</p>
              <p className="text-neutral-400 text-xs mt-0.5">
                {notifUnread > 0
                  ? `${notifUnread} non ${notifUnread === 1 ? "letta" : "lette"}`
                  : "Nessuna novità"}
              </p>
            </div>
            <span className="text-neutral-500 text-xs">›</span>
          </button>

          <button
            onClick={() => go("/chats")}
            className="flex items-center gap-4 w-full p-4 rounded-xl bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all text-left"
          >
            <div className="relative flex-shrink-0">
              <MessagesSquare size={22} className="text-brand-400" />
              {dmUnread > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-500 text-black text-[10px] font-extrabold flex items-center justify-center">
                  {dmUnread > 99 ? "99+" : dmUnread}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">Messaggi</p>
              <p className="text-neutral-400 text-xs mt-0.5">
                {dmUnread > 0
                  ? `${dmUnread} ${dmUnread === 1 ? "conversazione non letta" : "conversazioni non lette"}`
                  : "Nessun nuovo messaggio"}
              </p>
            </div>
            <span className="text-neutral-500 text-xs">›</span>
          </button>

          {/* FIX CERCHIA (Ago 2026): accesso rapido alla cerchia biker
              con badge delle richieste di connessione in sospeso */}
          <button
            onClick={() => go("/cerchia?tab=richieste")}
            className="flex items-center gap-4 w-full p-4 rounded-xl bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all text-left"
          >
            <div className="relative flex-shrink-0">
              <Users size={22} className="text-brand-400" />
              {connectionRequests > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center">
                  {connectionRequests > 99 ? "99+" : connectionRequests}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                Cerchia &amp; Affiliazioni
              </p>
              <p className="text-neutral-400 text-xs mt-0.5">
                {connectionRequests > 0
                  ? `${connectionRequests} ${connectionRequests === 1 ? "richiesta in sospeso" : "richieste in sospeso"}`
                  : "Bikers affiliati e richieste"}
              </p>
            </div>
            <span className="text-neutral-500 text-xs">›</span>
          </button>

          <div className="mt-1 pt-3 border-t border-white/10 flex flex-col gap-1">
            {/* FIX AGENT-MENU (Set 2026): accesso rapido area agente da mobile */}
            {sheetUser?.is_agent === true && (
              <button
                onClick={() => go("/agent/dashboard")}
                className="flex items-center gap-3 w-full px-2 py-2 text-emerald-400 hover:text-emerald-300 active:text-emerald-200 transition-colors text-sm"
              >
                <Shield size={18} />
                <span>Area Agente</span>
              </button>
            )}
            <button
              onClick={() => go("/profile")}
              className="flex items-center gap-3 w-full px-2 py-2 text-neutral-400 hover:text-white active:text-white transition-colors text-sm"
            >
              <UserRound size={18} />
              <span>Vai al profilo</span>
            </button>
            <ThemeToggleInline />
            {/* FIX: pulsante logout visibile su mobile per Rider */}
            <button
              onClick={() => {
                onClose();
                onLogout();
              }}
              className="flex items-center gap-3 w-full px-2 py-2 text-red-500 hover:text-red-400 active:text-red-300 transition-colors text-sm"
              aria-label="Esci dall'account"
            >
              <LogOut size={18} />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
